import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Table, Card, Button, Space, Popconfirm, Modal, Form, Input, Transfer, Tooltip, App } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { userManagementService } from "../../api/enhancedApi";
import { formatDate } from "../../utils/dateUtils";
import { useAuth } from "../../hooks/useAuth";
import useQuickSearch from "../../hooks/useQuickSearch";
import PermissionGuard from "../../components/auth/PermissionGuard";
import CollapsibleFilterBar from "../../components/common/CollapsibleFilterBar";
import RoleListFilter from "./components/RoleListFilter";

const { TextArea } = Input;

const RoleList = () => {
    const [roles, setRoles] = useState([]);
    const [allPermissions, setAllPermissions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formVisible, setFormVisible] = useState(false);
    const [permissionsVisible, setPermissionsVisible] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [form] = Form.useForm();
    const [editMode, setEditMode] = useState(false);
    const [targetKeys, setTargetKeys] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [filterParams, setFilterParams] = useState({});
    const { message } = App.useApp();
    const { hasPermission } = useAuth();

    // 新增分页状态
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 30,
        total: 0,
    });

    // 获取角色列表
    const fetchRoles = useCallback(async () => {
        setLoading(true);
        try {
            const roleParams = {
                page: pagination.current,
                page_size: pagination.pageSize,
            };
            if (filterParams.search) roleParams.search = filterParams.search;
            if (filterParams.permission_id) roleParams.permission_id = filterParams.permission_id;

            const rolesResponse = await userManagementService.fetchRoles(roleParams);
            if (rolesResponse && Array.isArray(rolesResponse.items) && typeof rolesResponse.total === "number") {
                setRoles(rolesResponse.items);
                setPagination((prev) => ({ ...prev, total: rolesResponse.total }));
            } else {
                message.error("获取角色数据格式不正确");
                console.error("Unexpected rolesData format:", rolesResponse);
                setRoles([]);
                setPagination((prev) => ({ ...prev, total: 0 }));
            }
        } catch (error) {
            message.error("获取数据失败: " + (error.message || "未知错误"));
            console.error(error);
            setRoles([]);
            setPagination((prev) => ({ ...prev, total: 0 }));
        } finally {
            setLoading(false);
        }
    }, [filterParams, pagination.current, pagination.pageSize, message]);

    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    useEffect(() => {
        let cancelled = false;
        const loadPermissions = async () => {
            try {
                const permissionsResponse = await userManagementService.fetchPermissions();
                if (cancelled) return;
                if (permissionsResponse && Array.isArray(permissionsResponse)) {
                    const formattedPermissions = permissionsResponse.map((permission) => ({
                        key: permission.id.toString(),
                        title: permission.name,
                        description: permission.description,
                        ...permission,
                    }));
                    setAllPermissions(formattedPermissions);
                } else {
                    message.error("获取权限列表格式不正确");
                    setAllPermissions([]);
                }
            } catch (error) {
                if (!cancelled) {
                    message.error("获取权限列表失败");
                    console.error(error);
                    setAllPermissions([]);
                }
            }
        };
        loadPermissions();
        return () => {
            cancelled = true;
        };
    }, [message]);

    const handleFilterSearch = useCallback((values) => {
        setFilterParams((prev) => {
            const next = {};
            if (prev.search) next.search = prev.search;
            if (values.permission_id) next.permission_id = values.permission_id;
            return next;
        });
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const quickSearch = useQuickSearch({ setFilterParams, setPagination });

    const handleFilterReset = useCallback(() => {
        quickSearch.clear();
        setFilterParams({});
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, [quickSearch]);

    const filterSummaryItems = useMemo(() => {
        if (!filterParams.permission_id) return [];
        const permission = allPermissions.find((item) => item.id === filterParams.permission_id);
        return [{ label: "权限", value: permission?.name || String(filterParams.permission_id) }];
    }, [filterParams.permission_id, allPermissions]);

    // 获取角色的权限
    const fetchRolePermissionData = async (roleId) => {
        try {
            const permissions = await userManagementService.fetchRolePermissions(roleId);
            const permissionIds = permissions.map((p) => p.id.toString());
            setTargetKeys(permissionIds);
        } catch (error) {
            message.error("获取角色权限失败");
            console.error(error);
        }
    };

    // 表格列定义
    const columns = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 60,
        },
        {
            title: "角色名称",
            dataIndex: "name",
            key: "name",
            width: 150,
        },
        {
            title: "描述",
            dataIndex: "description",
            key: "description",
            width: 300,
        },
        {
            title: "创建时间",
            dataIndex: "created_at",
            key: "created_at",
            width: 170,
            render: (date) => formatDate(date),
        },
        {
            title: "更新时间",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 170,
            render: (date) => formatDate(date),
        },
        {
            title: "操作",
            key: "action",
            width: 260,
            render: (_, record) => (
                <Space size="small">
                    <PermissionGuard permission="role:update">
                        <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
                            编辑
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="role:update">
                        <Button icon={<SafetyCertificateOutlined />} size="small" onClick={() => handleManagePermissions(record)}>
                            权限
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="role:delete">
                        <Popconfirm title="确定要删除此角色吗?" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
                            <Button danger icon={<DeleteOutlined />} size="small">
                                删除
                            </Button>
                        </Popconfirm>
                    </PermissionGuard>
                </Space>
            ),
        },
    ];

    // 打开新增角色表单
    const handleAdd = () => {
        // 检查创建权限
        if (!hasPermission("role:create")) {
            message.error("您没有创建角色的权限");
            return;
        }

        setEditMode(false);
        setSelectedRole(null);
        form.resetFields();
        setFormVisible(true);
    };

    // 打开编辑角色表单
    const handleEdit = (role) => {
        // 检查更新权限
        if (!hasPermission("role:update")) {
            message.error("您没有更新角色的权限");
            return;
        }

        setEditMode(true);
        setSelectedRole(role);
        form.setFieldsValue({
            name: role.name,
            description: role.description,
        });
        setFormVisible(true);
    };

    // 打开权限管理弹窗
    const handleManagePermissions = async (role) => {
        // 检查更新权限
        if (!hasPermission("role:update")) {
            message.error("您没有更新角色权限的权限");
            return;
        }

        setSelectedRole(role);
        await fetchRolePermissionData(role.id);
        setPermissionsVisible(true);
    };

    // 提交角色表单
    const handleFormSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (editMode) {
                // 检查更新权限
                if (!hasPermission("role:update")) {
                    message.error("您没有更新角色的权限");
                    return;
                }

                // 更新角色
                await userManagementService.updateRole(selectedRole.id, values);
                message.success("角色已更新");
            } else {
                // 检查创建权限
                if (!hasPermission("role:create")) {
                    message.error("您没有创建角色的权限");
                    return;
                }

                // 创建角色
                await userManagementService.createRole(values);
                message.success("角色已创建");
            }

            setFormVisible(false);
            fetchRoles();
        } catch (error) {
            console.error("表单提交错误:", error);
            message.error("提交角色失败，请检查输入");
        }
    };

    // 权限分配处理
    const handlePermissionChange = (nextTargetKeys) => {
        setTargetKeys(nextTargetKeys);
    };

    const handlePermissionSelectChange = (sourceSelectedKeys, targetSelectedKeys) => {
        setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys]);
    };

    // 提交权限分配
    const handlePermissionsSubmit = async () => {
        try {
            // 检查更新权限
            if (!hasPermission("role:update")) {
                message.error("您没有更新角色权限的权限");
                return;
            }

            const permissionIds = targetKeys.map((key) => parseInt(key, 10));
            await userManagementService.assignRolePermissions(selectedRole.id, { permission_ids: permissionIds });
            message.success("角色权限已更新");
            setPermissionsVisible(false);
            fetchRoles();
        } catch (error) {
            message.error("更新角色权限失败");
            console.error(error);
        }
    };

    // 删除角色
    const handleDelete = async (roleId) => {
        // 检查删除权限
        if (!hasPermission("role:delete")) {
            message.error("您没有删除角色的权限");
            return;
        }

        try {
            await userManagementService.deleteRole(roleId);
            message.success("角色已删除");
            fetchRoles();
        } catch (error) {
            if (error.response && error.response.status === 400) {
                message.error("该角色已被分配给用户，无法删除");
            } else {
                message.error("删除角色失败");
            }
            console.error(error);
        }
    };

    // 权限穿梭框渲染
    const renderPermissionItem = (item) => {
        const customLabel = (
            <Tooltip title={item.description}>
                <span>{item.title}</span>
            </Tooltip>
        );
        return {
            label: customLabel,
            value: item.title,
        };
    };

    // 处理表格分页、排序、筛选变化
    const handleTableChange = (newPagination, filters, sorter) => {
        setPagination((prev) => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));
        // 分页变化后由 pagination 依赖重新拉取列表
    };

    return (
        <div className="page-container">
            {/* 页面标题 */}
            <div className="page-header-compact">
                <div className="page-title-compact">角色管理</div>
                <div className="page-action-compact">
                    <PermissionGuard permission="role:create">
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                            添加角色
                        </Button>
                    </PermissionGuard>
                </div>
            </div>

            <CollapsibleFilterBar
                summaryItems={filterSummaryItems}
                onReset={handleFilterReset}
                drawerTitle="角色管理 - 筛选条件"
                quickControls={<Input placeholder="搜索角色名称 / 描述" style={{ width: 260, height: 26 }} {...quickSearch.inputProps} />}
            >
                <RoleListFilter onSearch={handleFilterSearch} permissions={allPermissions} autoApply />
            </CollapsibleFilterBar>

            {/* 角色列表 */}
            <Card className="data-card">
                <Table
                    dataSource={roles}
                    columns={columns}
                    rowKey="id"
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                    }}
                    loading={loading}
                    scroll={{ x: 1200 }}
                    size="small"
                    onChange={handleTableChange}
                />
            </Card>

            {/* 添加/编辑角色表单 */}
            <Modal title={editMode ? "编辑角色" : "添加角色"} open={formVisible} onOk={handleFormSubmit} onCancel={() => setFormVisible(false)} maskClosable={false} okText="保存" cancelText="取消">
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="角色名称" rules={[{ required: true, message: "请输入角色名称" }]}>
                        <Input placeholder="请输入角色名称" />
                    </Form.Item>

                    <Form.Item name="description" label="角色描述">
                        <TextArea rows={4} placeholder="请输入角色描述" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 权限管理弹窗 */}
            <Modal title={`分配权限: ${selectedRole?.name || ""}`} open={permissionsVisible} onOk={handlePermissionsSubmit} onCancel={() => setPermissionsVisible(false)} width={800} maskClosable={false} okText="保存" cancelText="取消">
                <Transfer
                    dataSource={allPermissions}
                    titles={["可用权限", "已分配权限"]}
                    targetKeys={targetKeys}
                    selectedKeys={selectedKeys}
                    onChange={handlePermissionChange}
                    onSelectChange={handlePermissionSelectChange}
                    render={renderPermissionItem}
                    listStyle={{
                        width: 350,
                        height: 400,
                    }}
                />
            </Modal>
        </div>
    );
};

export default RoleList;
